/**
 * GeolocationService - Detecção unificada de localização
 *
 * Cruza múltiplas fontes para máxima precisão:
 * - IP Geolocation (país real)
 * - Browser Language (preferência do usuário)
 * - Timezone (confirmação física)
 *
 * Usa score de confiança para resolver conflitos
 */

// Mapeamento de timezones para países
const TIMEZONE_COUNTRY_MAP = {
  // Brasil
  'America/Sao_Paulo': 'BR',
  'America/Rio_Branco': 'BR',
  'America/Manaus': 'BR',
  'America/Cuiaba': 'BR',
  'America/Fortaleza': 'BR',
  'America/Recife': 'BR',
  'America/Bahia': 'BR',
  'America/Belem': 'BR',

  // EUA
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'America/Phoenix': 'US',
  'America/Anchorage': 'US',
  'Pacific/Honolulu': 'US',

  // Portugal
  'Europe/Lisbon': 'PT',
  'Atlantic/Azores': 'PT',
  'Atlantic/Madeira': 'PT',

  // Espanha
  'Europe/Madrid': 'ES',
  'Atlantic/Canary': 'ES',

  // México
  'America/Mexico_City': 'MX',
  'America/Cancun': 'MX',
  'America/Tijuana': 'MX',

  // Argentina
  'America/Argentina/Buenos_Aires': 'AR',
  'America/Buenos_Aires': 'AR',

  // Outros LATAM
  'America/Lima': 'PE',
  'America/Bogota': 'CO',
  'America/Santiago': 'CL',
  'America/Montevideo': 'UY',

  // UK
  'Europe/London': 'GB',

  // Canadá
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'America/Montreal': 'CA',

  // Outros Europa
  'Europe/Paris': 'FR',
  'Europe/Berlin': 'DE',
  'Europe/Rome': 'IT'
};

// Mapeamento de idioma para país esperado
const LANGUAGE_COUNTRY_MAP = {
  'pt-BR': ['BR'],
  'pt-PT': ['PT', 'AO', 'MZ'],
  'pt': ['BR', 'PT'],
  'en-US': ['US', 'CA'],
  'en-GB': ['GB', 'IE', 'AU', 'NZ'],
  'en': ['US', 'GB', 'CA', 'AU'],
  'es-ES': ['ES'],
  'es-MX': ['MX'],
  'es-AR': ['AR'],
  'es': ['ES', 'MX', 'AR', 'CO', 'CL', 'PE']
};

// Mapeamento de país para locale padrão
const COUNTRY_LOCALE_MAP = {
  'BR': 'pt-BR',
  'PT': 'pt-PT',
  'AO': 'pt-BR',
  'MZ': 'pt-BR',
  'US': 'en-US',
  'GB': 'en-GB',
  'CA': 'en-US',
  'AU': 'en-US',
  'NZ': 'en-US',
  'IE': 'en-US',
  'ES': 'es-ES',
  'MX': 'es-ES',
  'AR': 'es-ES',
  'CO': 'es-ES',
  'CL': 'es-ES',
  'PE': 'es-ES',
  'UY': 'es-ES'
};

// Mapeamento de país para moeda
const COUNTRY_CURRENCY_MAP = {
  'BR': { currency: 'BRL', symbol: 'R$' },
  'US': { currency: 'USD', symbol: '$' },
  'CA': { currency: 'CAD', symbol: '$' },
  'GB': { currency: 'GBP', symbol: '£' },
  'PT': { currency: 'EUR', symbol: '€' },
  'ES': { currency: 'EUR', symbol: '€' },
  'FR': { currency: 'EUR', symbol: '€' },
  'DE': { currency: 'EUR', symbol: '€' },
  'IT': { currency: 'EUR', symbol: '€' },
  'MX': { currency: 'MXN', symbol: '$' },
  'AR': { currency: 'USD', symbol: '$' }, // Usar USD por instabilidade ARS
  'CL': { currency: 'USD', symbol: '$' },
  'CO': { currency: 'USD', symbol: '$' },
  'PE': { currency: 'USD', symbol: '$' },
  'UY': { currency: 'USD', symbol: '$' }
};

const CACHE_KEY = 'summdash_geolocation';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24h
const API_TIMEOUT = 4000; // 4s

class GeolocationService {
  constructor() {
    this.result = null;
    this.detecting = null;
  }

  /**
   * Detecta localização completa do usuário
   * @returns {Promise<{country, locale, currency, confidence, sources}>}
   */
  async detect() {
    // Evitar detecções paralelas
    if (this.detecting) return this.detecting;

    // Verificar cache
    const cached = this._getCache();
    if (cached) {
      this.result = cached;
      return cached;
    }

    this.detecting = this._detectFresh();
    const result = await this.detecting;
    this.detecting = null;

    return result;
  }

  /**
   * Detecção fresca sem cache
   */
  async _detectFresh() {
    // Coletar dados de múltiplas fontes em paralelo
    const [ipData, browserData, timezoneData] = await Promise.all([
      this._detectFromIP(),
      this._detectFromBrowser(),
      this._detectFromTimezone()
    ]);

    // Cruzar dados e calcular resultado
    const result = this._crossReference(ipData, browserData, timezoneData);

    // Cachear resultado
    this._setCache(result);
    this.result = result;

    return result;
  }

  /**
   * Detecta país via IP (fonte primária)
   */
  async _detectFromIP() {
    const apis = [
      {
        url: 'https://ipapi.co/json/',
        parse: (d) => ({ country: d.country_code, region: d.region, city: d.city })
      },
      {
        url: 'https://ip-api.com/json/?fields=countryCode,regionName,city',
        parse: (d) => ({ country: d.countryCode, region: d.regionName, city: d.city })
      }
    ];

    for (const api of apis) {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), API_TIMEOUT);

        const response = await fetch(api.url, { signal: controller.signal });
        clearTimeout(timeout);

        if (response.ok) {
          const data = await response.json();
          const parsed = api.parse(data);
          if (parsed.country) {
            return { success: true, ...parsed, source: 'ip' };
          }
        }
      } catch (e) {
        // Tentar próxima API
      }
    }

    return { success: false, source: 'ip' };
  }

  /**
   * Detecta preferência do navegador
   */
  _detectFromBrowser() {
    const lang = navigator.language || navigator.userLanguage || 'en-US';
    const languages = navigator.languages || [lang];

    // Extrair país do locale se presente (pt-BR -> BR)
    const primaryLang = lang.toLowerCase();
    const countryFromLang = lang.includes('-') ? lang.split('-')[1].toUpperCase() : null;

    return {
      success: true,
      language: lang,
      languages: languages,
      countryHint: countryFromLang,
      source: 'browser'
    };
  }

  /**
   * Detecta timezone para confirmação
   */
  _detectFromTimezone() {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const country = TIMEZONE_COUNTRY_MAP[tz] || null;

      return {
        success: !!country,
        timezone: tz,
        country: country,
        source: 'timezone'
      };
    } catch {
      return { success: false, source: 'timezone' };
    }
  }

  /**
   * Cruza dados das fontes e determina localização final
   */
  _crossReference(ipData, browserData, timezoneData) {
    let country = 'US';
    let confidence = 0;
    const sources = [];

    // 1. IP é a fonte mais confiável para país real
    if (ipData.success) {
      country = ipData.country;
      confidence += 50;
      sources.push('ip');
    }

    // 2. Verificar consistência com browser
    const browserLang = browserData.language.toLowerCase();
    const expectedCountries = this._getExpectedCountries(browserLang);

    if (ipData.success && expectedCountries.includes(ipData.country)) {
      // IP e browser concordam - alta confiança
      confidence += 30;
      sources.push('browser-match');
    } else if (!ipData.success && browserData.countryHint) {
      // Sem IP, usar hint do browser
      country = browserData.countryHint;
      confidence += 25;
      sources.push('browser-hint');
    } else if (ipData.success && !expectedCountries.includes(ipData.country)) {
      // Possível VPN ou expatriado
      // Se timezone confirma IP, confiar no IP
      // Se timezone diverge, dar peso ao browser
      if (timezoneData.success && timezoneData.country === ipData.country) {
        confidence += 20;
        sources.push('tz-confirms-ip');
      } else if (timezoneData.success && expectedCountries.includes(timezoneData.country)) {
        // Timezone confirma browser - possível VPN
        country = timezoneData.country;
        confidence += 15;
        sources.push('tz-vpn-detected');
      }
    }

    // 3. Timezone como confirmação adicional
    if (timezoneData.success && timezoneData.country === country) {
      confidence += 20;
      sources.push('tz-match');
    }

    // Determinar locale e moeda baseado no país final
    const locale = this._determineLocale(country, browserData.language, confidence);
    const currencyInfo = COUNTRY_CURRENCY_MAP[country] || { currency: 'USD', symbol: '$' };

    return {
      country,
      locale,
      currency: currencyInfo.currency,
      currencySymbol: currencyInfo.symbol,
      confidence: Math.min(confidence, 100),
      sources,
      raw: { ip: ipData, browser: browserData, timezone: timezoneData }
    };
  }

  /**
   * Retorna países esperados para um idioma
   */
  _getExpectedCountries(browserLang) {
    // Match exato primeiro
    if (LANGUAGE_COUNTRY_MAP[browserLang]) {
      return LANGUAGE_COUNTRY_MAP[browserLang];
    }

    // Match por prefixo
    const prefix = browserLang.split('-')[0];
    if (LANGUAGE_COUNTRY_MAP[prefix]) {
      return LANGUAGE_COUNTRY_MAP[prefix];
    }

    return [];
  }

  /**
   * Determina locale final priorizando país geográfico
   *
   * Lógica: Se temos certeza geográfica (IP + timezone), usar locale do país
   * Isso garante que brasileiro com browser pt-PT veja pt-BR
   * e português com browser pt-BR veja pt-PT
   */
  _determineLocale(country, browserLang, geoConfidence = 0) {
    const browserLangLower = browserLang.toLowerCase();
    const browserPrefix = browserLangLower.split('-')[0];

    // Se país é conhecido e temos confiança geográfica alta (>= 70%)
    // Priorizar locale do país quando idioma é compatível
    if (country && COUNTRY_LOCALE_MAP[country] && geoConfidence >= 70) {
      const countryLocale = COUNTRY_LOCALE_MAP[country];
      const countryLangPrefix = countryLocale.split('-')[0].toLowerCase();

      // Se browser fala o mesmo idioma base, usar locale do país
      // Ex: browser pt-PT + país BR = pt-BR (brasileiro com browser PT)
      // Ex: browser pt-BR + país PT = pt-PT (português com browser BR)
      if (browserPrefix === countryLangPrefix) {
        return countryLocale;
      }

      // Se idiomas diferentes, browser pode ser expatriado
      // Ex: browser en-US + país BR = usar en-US (americano no Brasil)
      // Mas verificar se é só variante regional do mesmo idioma base
    }

    // Sem confiança geográfica alta, usar preferência do browser
    const supportedLocales = ['pt-br', 'pt-pt', 'en-us', 'en-gb', 'es-es'];
    if (supportedLocales.includes(browserLangLower)) {
      return browserLang.replace(/^([a-z]{2})-([a-z]{2})$/i, (_, p1, p2) =>
        `${p1.toLowerCase()}-${p2.toUpperCase()}`
      );
    }

    // Usar mapeamento de país se disponível
    if (country && COUNTRY_LOCALE_MAP[country]) {
      return COUNTRY_LOCALE_MAP[country];
    }

    // Fallback por prefixo do idioma
    if (browserPrefix === 'pt') return 'pt-BR';
    if (browserPrefix === 'es') return 'es-ES';
    if (browserPrefix === 'en') return 'en-US';

    return 'en-US';
  }

  /**
   * Cache management
   */
  _getCache() {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const data = JSON.parse(cached);
      if (Date.now() - data.timestamp > CACHE_DURATION) {
        sessionStorage.removeItem(CACHE_KEY);
        return null;
      }

      return data.result;
    } catch {
      return null;
    }
  }

  _setCache(result) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({
        timestamp: Date.now(),
        result
      }));
    } catch {
      // Storage full or disabled
    }
  }

  /**
   * Limpa cache (útil para testes)
   */
  clearCache() {
    sessionStorage.removeItem(CACHE_KEY);
    this.result = null;
  }

  /**
   * Retorna resultado cacheado sem nova detecção
   */
  getCached() {
    if (this.result) return this.result;
    return this._getCache();
  }
}

// Singleton
export const geolocationService = new GeolocationService();

